import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { fetchProducts } from "../lib/shopify";
import ProductCard from "../components/ProductCard";
import EmptyShopBanner from "../components/EmptyShopBanner";
import { RARITIES } from "../lib/constants";
import { useReviewsSummary } from "../lib/useReviewsSummary";
import {
    classifyProduct,
    filterProductsByCategory,
} from "../lib/categorization";

export default function Shop() {
    const [params, setParams] = useSearchParams();
    const category = params.get("category") || "";
    const rarity = params.get("rarity") || "";
    const searchParam = params.get("q") || "";
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState(searchParam);
    const [sort, setSort] = useState("default");

    // Always fetch ALL products; category filtering is done client-side via
    // the automatic classifier so newly synced Shopify products get bucketed
    // without any manual code change.
    useEffect(() => {
        setLoading(true);
        fetchProducts({ first: 100 })
            .then((all) => setProducts(all))
            .catch((e) => console.error(e))
            .finally(() => setLoading(false));
    }, []);

    let visible = filterProductsByCategory(products, category);
    if (rarity) visible = visible.filter((p) => p.rarity === rarity);
    if (search.trim()) {
        const s = search.toLowerCase();
        visible = visible.filter(
            (p) =>
                p.name.toLowerCase().includes(s) ||
                p.character.toLowerCase().includes(s)
        );
    }
    if (sort === "price-asc") visible.sort((a, b) => a.price - b.price);
    if (sort === "price-desc") visible.sort((a, b) => b.price - a.price);
    if (sort === "name") visible.sort((a, b) => a.name.localeCompare(b.name));

    const reviewsMap = useReviewsSummary(
        visible.map((p) => p.handle).filter(Boolean)
    );

    function selectCategory(c) {
        const next = new URLSearchParams(params);
        if (c) next.set("category", c);
        else next.delete("category");
        setParams(next);
    }

    function selectRarity(r) {
        const next = new URLSearchParams(params);
        if (r) next.set("rarity", r);
        else next.delete("rarity");
        setParams(next);
    }

    const CATS = [
        { slug: "capitano", label: "Capitano" },
        { slug: "grande-rotta", label: "Grande Rotta" },
        { slug: "tesori", label: "Tesori" },
        { slug: "wanted", label: "Wanted" },
    ];

    const heading = category
        ? CATS.find((c) => c.slug === category)?.label.toUpperCase() ||
          "COLLEZIONE"
        : rarity
          ? rarity === "Esclusivo"
              ? "TESORI ESCLUSIVI"
              : rarity === "Pre-ordine"
                ? "WANTED · PRE-ORDINI"
                : rarity.toUpperCase()
          : "TUTTE LE FIGURE";

    return (
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-12" data-testid="shop-page">
            <div className="border-b-2 border-ink pb-8 mb-10">
                <div className="font-hand text-2xl text-bounty">il bottino</div>
                <h1 className="font-display text-6xl lg:text-7xl tracking-tight">
                    {heading}
                </h1>
                <p className="mt-3 text-ink/70 max-w-xl">
                    {loading ? "Caricamento dal Grand Line..." : `${visible.length} action figures dallo store Shopify`}
                </p>
            </div>

            {!loading && products.length === 0 && (
                <EmptyShopBanner />
            )}

            <div className="grid lg:grid-cols-4 gap-8">
                {/* Filters sidebar */}
                <aside className="lg:col-span-1 space-y-6">
                    <div className="bounty-card p-5">
                        <div className="font-display text-xl tracking-wider mb-3">
                            CERCA
                        </div>
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Luffy, Zoro, Ace..."
                            className="input-manga"
                            data-testid="shop-search-input"
                        />
                    </div>

                    <div className="bounty-card p-5">
                        <div className="font-display text-xl tracking-wider mb-3">
                            CATEGORIA
                        </div>
                        <div className="space-y-2">
                            <button
                                onClick={() => selectCategory("")}
                                className={`block w-full text-left px-3 py-2 border-2 border-ink font-mono text-sm transition-manga ${!category ? "bg-ink text-parchment" : "bg-white hover:bg-seafoam"}`}
                                data-testid="filter-cat-all"
                            >
                                Tutte
                            </button>
                            {CATS.map((c) => (
                                <button
                                    key={c.slug}
                                    onClick={() => selectCategory(c.slug)}
                                    className={`block w-full text-left px-3 py-2 border-2 border-ink font-mono text-xs uppercase transition-manga ${category === c.slug ? "bg-bounty text-parchment" : "bg-white hover:bg-seafoam"}`}
                                    data-testid={`filter-cat-${c.slug}`}
                                >
                                    {c.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="bounty-card p-5">
                        <div className="font-display text-xl tracking-wider mb-3">
                            RARITÀ
                        </div>
                        <div className="space-y-2">
                            <button
                                onClick={() => selectRarity("")}
                                className={`block w-full text-left px-3 py-2 border-2 border-ink font-mono text-sm transition-manga ${!rarity ? "bg-ink text-parchment" : "bg-white hover:bg-seafoam"}`}
                                data-testid="filter-rarity-all"
                            >
                                Tutte
                            </button>
                            {RARITIES.map((r) => (
                                <button
                                    key={r}
                                    onClick={() => selectRarity(r)}
                                    className={`block w-full text-left px-3 py-2 border-2 border-ink font-mono text-xs transition-manga ${rarity === r ? "bg-gold text-ink" : "bg-white hover:bg-seafoam"}`}
                                    data-testid={`filter-rarity-${r}`}
                                >
                                    {r}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="bounty-card p-5">
                        <div className="font-display text-xl tracking-wider mb-3">
                            ORDINA
                        </div>
                        <select
                            value={sort}
                            onChange={(e) => setSort(e.target.value)}
                            className="input-manga"
                            data-testid="shop-sort-select"
                        >
                            <option value="default">Predefinito</option>
                            <option value="price-asc">Prezzo: crescente</option>
                            <option value="price-desc">Prezzo: decrescente</option>
                            <option value="name">Nome A-Z</option>
                        </select>
                    </div>
                </aside>

                {/* Grid */}
                <div className="lg:col-span-3">
                    {loading ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {Array.from({ length: 6 }).map((_, i) => (
                                <div
                                    key={i}
                                    className="bounty-card aspect-[4/5] bg-seafoam animate-pulse"
                                />
                            ))}
                        </div>
                    ) : visible.length === 0 ? (
                        <div className="text-center py-20 bounty-card">
                            <div className="font-hand text-3xl text-bounty">
                                Niente trovato!
                            </div>
                            <p className="mt-2 text-ink/70">
                                Cambia i filtri o esplora un&apos;altra ciurma.
                            </p>
                        </div>
                    ) : (
                        <motion.div
                            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
                            data-testid="shop-grid"
                            initial="hidden"
                            animate="visible"
                            variants={{
                                hidden: {},
                                visible: {
                                    transition: { staggerChildren: 0.05 },
                                },
                            }}
                        >
                            {visible.map((p) => (
                                <motion.div
                                    key={p.id}
                                    variants={{
                                        hidden: { opacity: 0, y: 20 },
                                        visible: { opacity: 1, y: 0 },
                                    }}
                                >
                                    <ProductCard
                                        product={p}
                                        reviewSummary={reviewsMap[p.handle]}
                                    />
                                </motion.div>
                            ))}
                        </motion.div>
                    )}
                </div>
            </div>
        </div>
    );
}
